/**
 * <athos-carousel>: a Searchspring-driven rail that subclasses
 * <carousel-slider>. On connect it:
 *   1. Fetches the configured Searchspring profile.
 *   2. If empty or the request fails, dispatches carousel:empty so the section
 *      can reveal a server-rendered fallback collection rail (preferred) or
 *      hide the tab entirely.
 *   3. Otherwise hydrates the inner <template> once per result and hands off to
 *      the parent class via forceInit().
 *
 * Before hydration the empty track inherits a min-height from --slides-to-show
 * and the product-card image ratio plus --card-details-reserve (see the
 * carousel snippet's stylesheet), so the section reserves the right space and
 * avoids CLS.
 *
 * Bind syntax:
 *   data-bind="key"            -> el.textContent = data[key]
 *   data-bind-attr="a:k|b:k2"  -> attribute assignments
 *   data-bind-loop="key"       -> clone inner <template> per item
 *   data-bind-if="key"         -> remove element when value is falsy
 *
 * Bindings only use textContent and attribute setters, so API
 * output cannot inject markup.
 *
 * Required attributes: data-athos-id (profile id), data-currency-format
 * (shop money_format). Optional: data-product-sku (anchor SKU for PDP "similar
 * items"), data-shopper-id (authenticated customer id).
 */
(function () {
  if (window.customElements.get("athos-carousel")) return;

  // The base class is loaded with `defer` ahead of us in layout/theme.liquid,
  // so it is normally already registered. whenDefined() covers any exotic
  // loading order without polling.
  if (window.customElements.get("carousel-slider")) {
    define();
  } else {
    window.customElements.whenDefined("carousel-slider").then(define);
  }

  function define() {
    if (window.customElements.get("athos-carousel")) return;
    const Carousel = window.customElements.get("carousel-slider");

    class AthosCarousel extends Carousel {
      constructor() {
        super();
        this._hydrated = false;
        this._hydrating = false;
      }

      connectedCallback() {
        if (this._hydrated || this._hydrating || !this.isConnected) return;
        // Don't kick off the fetch from a hidden tab; wait for the section's
        // tab switcher to call forceInit() when the panel becomes visible.
        // Eager rails (above the fold, first tab) skip this gate.
        if (this.clientWidth === 0 && !this.hasAttribute("eager")) return;
        if (this.hasAttribute("defer-hydrate")) return;
        this._startHydrate();
      }

      forceInit() {
        if (this._hydrated) {
          if (typeof super.forceInit === "function") super.forceInit();
          return;
        }
        if (this._hydrating) return;
        this._startHydrate();
      }

      _startHydrate() {
        this._hydrating = true;
        const id = this.dataset.athosId;
        const mark = id ? `ss:${id}` : "ss";
        if (typeof performance !== "undefined" && performance.mark) {
          try {
            performance.mark(`${mark}:start`);
          } catch (_e) {
            /* ignore mark-name collision */
          }
        }
        this._hydrate()
          .then(() => {
            if (typeof performance !== "undefined" && performance.mark) {
              try {
                performance.mark(`${mark}:end`);
                performance.measure(mark, `${mark}:start`, `${mark}:end`);
              } catch (_e) {
                /* ignore */
              }
            }
          })
          .catch((err) => {
            // Network failure: fall back to the same :empty path so the section
            // can reveal its server-rendered fallback collection (or hide the
            // tab).
            this._hydrating = false;
            // Silent catch
            this._fail();
          });
      }

      async _hydrate() {
        const id = this.dataset.athosId;
        if (!id) return this._fail();

        if (!window.AthosAPI || !window.ProductCardBind) {
          console.warn(
            "athos-carousel: missing AthosAPI/ProductCardBind",
          );
          return this._fail();
        }

        const profile = await this._fetch(id);
        if (!profile || !profile.results || profile.results.length === 0)
          return this._fail();

        const { tag, responseId, results } = profile;
        await this._primePresentmentPrices(results);

        const template = this.querySelector(
          'template[data-js="card-template"]',
        );
        const track = this.querySelector(':scope > [data-js="carousel-track"]');
        if (!template || !track) return this._fail();

        const format = this.dataset.currencyFormat || "{{amount}}";
        // Optional cap on inner-slider images. data-single-image is shorthand
        // for data-max-media="1", handy in compact contexts (e.g. the minicart)
        // where the inner swipe is just noise.
        const maxMedia = this.hasAttribute("data-single-image")
          ? 1
          : parseInt(this.dataset.maxMedia, 10) || 0;

        const quickAddFirst = this.hasAttribute("data-quick-add-first");
        const revealCards = this.hasAttribute("data-reveal-cards");

        // Clear any server-rendered ghost placeholders now that real cards
        // are ready to mount. No-op for rails that ship without skeletons.
        track
          .querySelectorAll('[data-js="carousel-skeleton"]')
          .forEach((el) => el.remove());

        results.forEach((result, index) => {
          const data = this._normalise(result, format);
          if (maxMedia > 0 && Array.isArray(data.media)) {
            data.media = data.media.slice(0, maxMedia);
          }
          const card = window.ProductCardBind.renderCard(template, data);
          if (!card) return;
          if (revealCards) {
            // Drives the motion-safe staggered fade-in (see minicart CSS).
            card.dataset.revealing = "true";
            card.style.setProperty("--reveal-index", String(index));
          }
          if (quickAddFirst) {
            card.setAttribute("data-quick-add-first", "");
            card.querySelectorAll("a[href]").forEach((a) => {
              a.removeAttribute("href");
            });
            // Bind the first available variant so a one-tap add (e.g. the
            // minicart recommendation rail) can skip the product-JSON fetch.
            const variants = Array.isArray(data.variants) ? data.variants : [];
            const chosen = variants.find((v) => v.available) || variants[0];
            if (chosen && chosen.id)
              card.setAttribute("data-variant-id", String(chosen.id));
          }
          track.appendChild(card);
        });

        if (window.AthosBeacon && responseId) {
          window.AthosBeacon.sendRecommendRender(tag, responseId);

          track.addEventListener("click", function (e) {
            if (!e.target.closest("a[href]")) return;
            const card = e.target.closest('[data-js="product-card"]');
            if (!card) return;
            const handle = card.getAttribute("data-product-handle");
            const result = results.find(function (r) {
              const rHandle =
                (r.attributes && r.attributes.handle) || r.handle || "";
              return rHandle === handle;
            });
            if (!result) return;
            window.AthosBeacon.sendRecommendClickthrough(
              tag,
              responseId,
              result,
            );
          });

          track.addEventListener("quick-add:added", function (e) {
            const card = e.target.closest('[data-js="product-card"]');
            if (!card) return;
            const handle = card.getAttribute("data-product-handle");
            const result = results.find(function (r) {
              const rHandle =
                (r.attributes && r.attributes.handle) || r.handle || "";
              return rHandle === handle;
            });
            if (!result) return;
            const addedItem = e.detail && e.detail.item ? e.detail.item : null;
            window.AthosBeacon.sendRecommendAddtocart(
              tag,
              responseId,
              result,
              addedItem,
            );
          });
        }

        // Reveal the host if it was hidden by the section's empty
        // handler on a previous hydrate attempt. No-op otherwise.
        this.removeAttribute("hidden");

        this._hydrated = true;
        this._hydrating = false;
        // Now the track has children, hand off to the parent class so
        // it measures and binds scroll listeners.
        if (typeof super.forceInit === "function") super.forceInit();
        else super.connectedCallback();

        this._observeImpressions(tag, responseId, results, track);
      }

      /**
       * Fetch a single Searchspring profile.
       * @param {string} profileId
       * @returns {Promise<object[]>}
       */
      async _fetch(profileId) {
        const products = this.dataset.productSku
          ? [this.dataset.productSku]
          : [];
        const shopper = this.dataset.shopperId || undefined;
        const lastViewed = this._cookieSkuList("athosViewedProducts", 15);
        const cart = this._cookieSkuList("athosCartProducts", 25);

        const profiles = await window.AthosAPI.fetchRecommend({
          profiles: [profileId],
          products,
          shopper,
          lastViewed,
          cart,
        });
        return profiles && profiles.length > 0 ? profiles[0] : null;
      }

      /**
       * Read a tracking cookie of comma-separated SKUs into a deduped,
       * length-capped array of SKUs for the recommend API.
       * @param {string} cookieName - The tracking cookie to read.
       * @param {number} limit - Maximum SKUs to keep, most recent first.
       * @returns {string[]|undefined} The SKUs, or undefined when empty/unreadable.
       */
      _cookieSkuList(cookieName, limit) {
        try {
          const raw = window.AthosAPI.getCookie(cookieName);
          if (!raw) return undefined;
          const skus = raw
            .split(",")
            .filter(Boolean)
            .filter((sku, index, self) => self.indexOf(sku) === index)
            .slice(0, limit);
          return skus.length > 0 ? skus : undefined;
        } catch (e) {
          return undefined;
        }
      }

      /**
       * Prime the Storefront presentment-price cache for the profile's
       * results so `_normalise` can overlay exact market prices
       * synchronously. No-op for base-currency shoppers.
       *
       * @param {object[]} results - The raw API result objects.
       * @returns {Promise<void>}
       */
      async _primePresentmentPrices(results) {
        if (typeof window.AthosAPI.fetchPresentmentPrices !== "function") return;
        const handles = (results || [])
          .map((r) => (r.attributes && r.attributes.handle) || r.handle || "")
          .filter(Boolean);
        if (handles.length === 0) return;
        await window.AthosAPI.fetchPresentmentPrices({ handles });
      }

      /**
       * Observe rendered cards with IntersectionObserver and fire impression
       * beacons for items that enter the viewport. Each card fires at most once.
       *
       * @param {string} tag - The recommendation profile tag.
       * @param {string} responseId - The API responseId.
       * @param {object[]} results - The raw API result objects (in render order).
       * @param {Element} track - The carousel track element containing the cards.
       * @returns {void}
       */
      _observeImpressions(tag, responseId, results, track) {
        if (!window.AthosBeacon || !responseId) return;
        if (!("IntersectionObserver" in window)) {
          // Fallback: fire all as impressed immediately
          window.AthosBeacon.sendRecommendImpression(tag, responseId, results);
          return;
        }

        const cards = Array.from(track.children);
        const seen = new Set();

        const observer = new IntersectionObserver(
          function (entries) {
            const visible = [];
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              const index = cards.indexOf(entry.target);
              if (index === -1 || seen.has(index)) return;
              seen.add(index);
              observer.unobserve(entry.target);
              if (results[index]) {
                visible.push(results[index]);
              }
            });
            if (visible.length > 0)
              window.AthosBeacon.sendRecommendImpression(
                tag,
                responseId,
                visible,
              );
          },
          { threshold: 0.5 },
        );

        cards.forEach(function (card) {
          observer.observe(card);
        });
      }

      _fail() {
        this._hydrating = false;
        // Drop ghost placeholders so a hidden/fallback rail never strands them.
        const track = this.querySelector(':scope > [data-js="carousel-track"]');
        if (track) {
          track
            .querySelectorAll('[data-js="carousel-skeleton"]')
            .forEach((el) => el.remove());
        }
        this.dispatchEvent(
          new CustomEvent("carousel:empty", {
            bubbles: true,
            composed: true,
          }),
        );
      }

      /**
       * Whitelist a colour token from API data for use inside a style
       * attribute. Accepts hex, rgb()/rgba()/hsl()/hsla(), and simple CSS
       * colour keywords; anything else falls back.
       * @param {unknown} value
       * @param {string} fallback
       * @returns {string}
       */
      _safeColour(value, fallback) {
        const v = String(value ?? "").trim();
        if (
          /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v) ||
          /^(?:rgb|rgba|hsl|hsla)\([\d\s.,%\/]+\)$/i.test(v) ||
          /^[a-z]{3,20}$/i.test(v)
        ) {
          return v;
        }
        return fallback;
      }

      /**
       * Map a Searchspring result onto the property names the
       * `<template>` data-bind directives reference.
       * @param {object} r
       * @param {string} format
       */
      _normalise(r, format) {
        const core = (r.mappings && r.mappings.core) || {};
        const attrs = r.attributes || {};
        const resultHandle = attrs.handle || r.handle || "";

        // Prices arrive in major units. Convert to cents.
        const priceMajor = parseFloat(core.price ?? r.price ?? 0);
        const msrpMajor = parseFloat(core.msrp ?? r.msrp ?? 0);
        let price = Number.isFinite(priceMajor)
          ? Math.round(priceMajor * 100)
          : 0;
        let compareAt = Number.isFinite(msrpMajor)
          ? Math.round(msrpMajor * 100)
          : 0;
        // Athos indexes base-currency (AUD) feed prices; market shoppers get
        // the exact presentment price primed from the Storefront API instead.
        const presentment =
          typeof window.AthosAPI.getPresentmentPrice === "function"
            ? window.AthosAPI.getPresentmentPrice(resultHandle)
            : null;
        if (presentment) {
          price = presentment.price;
          compareAt = presentment.compareAt;
        }
        const onSale = compareAt > 0 && compareAt > price;
        const promo =
          window.PromoPricing && typeof window.PromoPricing.resolve === "function"
            ? window.PromoPricing.resolve({ priceCents: price, tags: attrs.ss_tags || r.ss_tags })
            : null;

        const imageList =
          (Array.isArray(attrs.ss_images) && attrs.ss_images) ||
          (Array.isArray(r.images) && r.images) ||
          [core.imageUrl || core.thumbnailImageUrl].filter(Boolean);

        const titleParts = (core.name || r.name || r.title || "").split(" - ");
        const title = titleParts[0];
        const colour = titleParts[1] || "";
        const handle = resultHandle;
        const productUrl =
          core.url || r.url || (handle ? `/products/${handle}` : "#");

        // Shopify's auto-generated video posters (/files/preview_images/…)
        // stay in the list: they render as still slides that
        // `assets/product-card-video.js` upgrades to playing video in place.
        const media = imageList.map((src) => {
            let imageUrl = src;
            let srcset = "";
            if (imageUrl && imageUrl.includes("cdn.shopify.com")) {
              try {
                const url = new URL(imageUrl, window.location.origin);
                // Strip existing size suffixes like _100x100 or _small before adding width parameter
                url.pathname = url.pathname.replace(
                  /_(?:\d+x\d*(?:_crop_[a-zA-Z]+)?|small|medium|large|grande|original)(\.[a-zA-Z0-9]+)$/i,
                  "$1",
                );

                srcset = [400, 600, 800, 1200]
                  .map((w) => {
                    url.searchParams.set("width", String(w));
                    return `${url.toString()} ${w}w`;
                  })
                  .join(", ");

                url.searchParams.set("width", "800");
                imageUrl = url.toString();
              } catch (e) {}
            }
            return {
              image: imageUrl,
              alt: title,
              width: 200,
              height: 290,
              product_url: productUrl,
              product_title: title,
              is_image: "true",
              srcset: srcset || undefined,
              sizes_attr: "(max-width: 767px) 84vw, 24vw",
            };
          });

        // Sizes. Searchspring exposes a JSON-encoded array of
        // { label, id, available } objects in attrs.ss_sizes. Mirrors the
        // size-derivation in product-card.liquid: strip the locale prefix
        // (e.g. "CA ") to get the bare size; "One Size" stays as a single
        // non-prefixed value.
        let parsedSizes = [];
        const rawSizes = attrs.ss_sizes;
        if (typeof rawSizes === "string" && rawSizes) {
          try {
            parsedSizes = JSON.parse(rawSizes);
          } catch (_e) {
            parsedSizes = [];
          }
        } else if (Array.isArray(rawSizes)) {
          parsedSizes = rawSizes;
        }
        parsedSizes = Array.isArray(parsedSizes)
          ? parsedSizes.filter((s) => s && s.label)
          : [];

        let sizeLabel = "";
        if (parsedSizes.length > 0) {
          const firstLabel = String(parsedSizes[0].label || "");
          if (
            firstLabel &&
            firstLabel !== "One Size" &&
            firstLabel.includes(" ")
          ) {
            const parts = firstLabel.split(" ");
            parts.pop();
            sizeLabel = parts.join(" ").trim();
          }
        }

        const variants = parsedSizes.map((s) => {
          const label = String(s.label || "");
          let size;
          if (label === "One Size") {
            size = label;
          } else if (label.includes(" ")) {
            const parts = label.split(" ");
            size = (parts[parts.length - 1] || label).trim();
          } else {
            size = label;
          }
          const available = Number(s.available) > 0;
          return {
            id: String(s.id || ""),
            label,
            size,
            available,
            available_str: available ? "true" : "false",
            // Only set when unavailable so the binder skips the
            // attribute on available buttons (it ignores undefined).
            aria_disabled: available ? undefined : "true",
            aria_label: label,
          };
        });

        const rawBanners =
          attrs.mfield_custom_urgency_banners ||
          r.mfield_custom_urgency_banners ||
          attrs.custom_urgency_banners ||
          r.custom_urgency_banners ||
          attrs.urgency_banners ||
          r.urgency_banners;
        let parsedBanners = [];
        if (typeof rawBanners === "string" && rawBanners) {
          try {
            const parsed = JSON.parse(
              rawBanners.replace(/&quot;/g, '"').replace(/&amp;/g, "&"),
            );
            parsedBanners = Array.isArray(parsed) ? parsed : [parsed];
          } catch (_e) {
            parsedBanners = [];
          }
        } else if (Array.isArray(rawBanners)) {
          parsedBanners = rawBanners;
        } else if (typeof rawBanners === "object" && rawBanners !== null) {
          parsedBanners = [rawBanners];
        }

        let banners = [];
        if (Array.isArray(parsedBanners)) {
          parsedBanners = parsedBanners.map((b) => {
            if (typeof b === "string") {
              try {
                return JSON.parse(
                  b.replace(/&quot;/g, '"').replace(/&amp;/g, "&"),
                );
              } catch (_e) {
                return null;
              }
            }
            return b;
          });
          const nowMs = Date.now();
          banners = parsedBanners
            .filter((b) => {
              if (!b || !b.text || b.paused === true || b.show === false)
                return false;
              if (b.scheduledStartAt) {
                const startMs = Date.parse(b.scheduledStartAt);
                if (!isNaN(startMs) && nowMs < startMs) return false;
              }
              if (b.scheduledEndAt) {
                const endMs = Date.parse(b.scheduledEndAt);
                if (!isNaN(endMs) && nowMs > endMs) return false;
              }
              return true;
            })
            .map((b) => ({
              text: String(b.text || ""),
              style_str: `background-color: ${this._safeColour(b.bg_color, "#FCFBF8")}; color: ${this._safeColour(b.text_color, "#050000")};`,
            }));
        }

        const ssBadgeText = window.AthosAPI.cleanBannerText(
          attrs.ss_badge_text || r.ss_badge_text,
        );
        if (ssBadgeText) {
          const ssBadgeBg = this._safeColour(
            attrs.ss_badge_bg || r.ss_badge_bg,
            "#FCFBF8",
          );
          const ssBadgeColour = this._safeColour(
            attrs.ss_badge_colour || r.ss_badge_colour,
            "#050000",
          );
          banners.push({
            text: ssBadgeText,
            style_str:
              "background-color: " +
              ssBadgeBg +
              "; color: " +
              ssBadgeColour +
              ";",
          });
        }

        const rawProductLabel =
          attrs.mfield_custom_product_label || r.mfield_custom_product_label;
        let parsedProductLabel = "";
        if (typeof rawProductLabel === "string") {
          parsedProductLabel = window.AthosAPI.cleanBannerText(rawProductLabel);
        } else if (
          Array.isArray(rawProductLabel) &&
          rawProductLabel.length > 0
        ) {
          parsedProductLabel =
            typeof rawProductLabel[0] === "string" ? rawProductLabel[0] : "";
        }

        if (typeof parsedProductLabel === "string" && parsedProductLabel) {
          const labelParts = parsedProductLabel.split("|");
          const labelText = (labelParts[0] || "").trim();
          if (labelText) {
            const labelBg = this._safeColour(
              (labelParts[1] || "").trim(),
              "#FCFBF8",
            );
            const labelColor = this._safeColour(
              (labelParts[2] || "").trim(),
              "#050000",
            );
            banners.push({
              text: labelText,
              style_str: `background-color: ${labelBg}; color: ${labelColor};`,
            });
          }
        }

        const seenBannerText = {};
        banners = banners.filter(function (b) {
          const key = (b.text || "").trim().toLowerCase();
          if (!key || seenBannerText[key]) return false;
          // Text still shaped like serialised JSON or carrying entities failed
          // to decode upstream — drop it rather than render raw markup.
          if (
            key.startsWith('["') ||
            key.startsWith("[{") ||
            key.startsWith('{"') ||
            key.indexOf("&quot;") !== -1
          ) {
            return false;
          }
          seenBannerText[key] = true;
          return true;
        });

        const { colour_options, colour_overflow } =
          window.ProductCardBind.parseColourSwatches(
            r,
            handle,
            productUrl,
            colour,
          );

        return {
          id: core.parentId || r.parentId || core.uid || r.id || r.product_id || "",
          handle,
          title,
          colour,
          product_url: productUrl,
          product_title: title,
          price,
          compare_at_price: compareAt,
          on_sale: onSale,
          price_type: onSale ? "discount" : null,
          saved_percent: onSale
            ? Math.floor(((compareAt - (promo ? promo.priceCents : price)) / compareAt) * 100)
            : 0,
          promo_percent: promo ? promo.percent : 0,
          promo_price: promo ? promo.priceCents : price,
          formatted_promo_price: promo
            ? window.AthosAPI.formatMoney(promo.priceCents, format, true)
            : null,
          promo_message: promo
            ? window.PromoPricing.message(
                window.AthosAPI.formatMoney(promo.priceCents, format, true),
                promo.percent,
              ) || null
            : null,
          formatted_price: window.AthosAPI.formatMoney(price, format, true),
          formatted_compare_at_price: window.AthosAPI.formatMoney(
            compareAt,
            format,
            true,
          ),
          variants,
          size_label: sizeLabel,
          colour_options,
          colour_overflow,
          banners: banners,
          yotpo_bottomline_raw:
            attrs.mfield_yotpo_preloaded_bottomline ||
            r.mfield_yotpo_preloaded_bottomline ||
            null,
          hover_image: media[1] ? media[1].image || null : null,
          media:
            media.length > 0
              ? media
              : [
                  {
                    image: "",
                    alt: title,
                    width: 200,
                    height: 290,
                    product_url: productUrl,
                    product_title: title,
                    is_image: "true",
                  },
                ],
        };
      }
    }

    window.customElements.define("athos-carousel", AthosCarousel);
  }
})();
